import { useRef, useState, useCallback, useEffect } from 'react';
import type { Entity } from '../hooks/useFeed/types';
import { useDispatch } from 'react-redux';
import { updateUserProfile } from '@user/redux/routines';
import useUser from '@/hooks/useUser';
export enum FeedSortingOptions {
  Newest = 'newest',
  Return7D = '7D',
  Return1Y = '1Y',
}

interface SortOption {
  option: FeedSortingOptions;
  label: string;
}

interface Response {
  options: SortOption[];
  handleSelect: (value: string) => void;
  handleSorting: (a: string, b: string) => number;
  selectedSortType?: FeedSortingOptions;
}

export default function useFeedSorting(
  feedEntities: Record<string, Entity>
): Response {
  const { data: user } = useUser();
  const { defaultFeedSorting } = user;
  const feedSortingOptionsRef = useRef([
    {
      option: FeedSortingOptions.Newest,
      label: 'Newest',
    },
    {
      option: FeedSortingOptions.Return7D,
      label: '7 Day Return',
    },
    {
      option: FeedSortingOptions.Return1Y,
      label: '1 Year Return',
    },
  ]);
  const dispatch = useDispatch();
  const [selectedSortType, setSelectedSortType] = useState(defaultFeedSorting);

  useEffect(() => {
    if (
      typeof selectedSortType === 'undefined' &&
      typeof defaultFeedSorting !== 'undefined'
    ) {
      setSelectedSortType(defaultFeedSorting);
    }
  }, [selectedSortType, defaultFeedSorting]);

  const handleSelect = useCallback(
    (value: string) => {
      if (value !== selectedSortType) {
        setSelectedSortType(value as FeedSortingOptions);
        dispatch(
          updateUserProfile.trigger({
            settings: {
              feedSorting: value,
            },
          })
        );
      }
    },
    [dispatch, selectedSortType]
  );

  const handleSorting = useCallback(
    (a: string, b: string) => {
      const entityA = feedEntities[a];
      const entityB = feedEntities[b];
      if (selectedSortType === FeedSortingOptions.Newest) {
        const itemA = entityA.feedItems[0];
        const itemB = entityB.feedItems[0];
        if (typeof itemA === 'undefined' || typeof itemB === 'undefined') {
          return 0;
        }
        const dateA = itemA.date;
        const dateB = itemB.date;
        if (dateA === null || dateB === null) return 0;
        return dateB - dateA;
      }
      if (selectedSortType === FeedSortingOptions.Return7D) {
        if (entityA.return7D === null) return 1;
        if (entityB.return7D === null) return -1;
        return entityB.return7D - entityA.return7D;
      }
      if (selectedSortType === FeedSortingOptions.Return1Y) {
        if (entityA.return1YrAbs === null) return 1;
        if (entityB.return1YrAbs === null) return -1;
        return entityB.return1YrAbs - entityA.return1YrAbs;
      }
      return 0;
    },
    [selectedSortType, feedEntities]
  );

  return {
    options: feedSortingOptionsRef.current,
    handleSelect,
    handleSorting,
    selectedSortType,
  };
}
